import { useState } from "react";
import Select from "@/components/base/Select";
import { reviewSummary, reviews } from "@/mocks/reviews";
import type { Review } from "@/mocks/reviews";

const ratingFilters = ["الكل", "5★", "4★", "3★", "2★", "1★"];
const serviceOptions = [
  { value: "كل الخدمات", label: "كل الخدمات" },
  { value: "غسيل سيارات", label: "غسيل سيارات" },
  { value: "تنظيف منازل", label: "تنظيف منازل" },
  { value: "مغاسل ملابس", label: "مغاسل ملابس" },
];
const sortOptions = [
  { value: "الأحدث", label: "الأحدث" },
  { value: "الأقدم", label: "الأقدم" },
];

function Stars({ rating, size = "sm" }: { rating: number; size?: "sm" | "lg" }) {
  const iconSize = size === "lg" ? "text-xl" : "text-sm";
  return (
    <div className="flex items-center" dir="ltr">
      {[1, 2, 3, 4, 5].map((i) => (
        <i
          key={i}
          className={`ri-star-fill ${iconSize} ${i <= rating ? "text-[#F59E0B]" : "text-[#D5E3E3]"}`}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [filter, setFilter] = useState("الكل");
  const [service, setService] = useState("كل الخدمات");
  const [sort, setSort] = useState("الأحدث");
  const [replyFor, setReplyFor] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");
  const [items, setItems] = useState<Review[]>(reviews);

  const filtered = items.filter((r) => {
    const matchRating = filter === "الكل" || r.rating === Number(filter.replace("★", ""));
    const matchService = service === "كل الخدمات" || r.service.includes(service.replace(" — ", ""));
    return matchRating && matchService;
  });

  const sorted = [...filtered].sort((a, b) =>
    sort === "الأحدث" ? b.id - a.id : a.id - b.id,
  );

  const sendReply = (id: number) => {
    if (!replyText.trim()) return;
    setItems((prev) =>
      prev.map((r) => (r.id === id ? { ...r, replied: true, reply: replyText.trim() } : r)),
    );
    setReplyText("");
    setReplyFor(null);
  };

  return (
    <div>
      <h1 className="text-[28px] font-bold text-ghasla-text-heading">التقييمات</h1>

      {/* Summary */}
      <div className="mt-6 flex flex-col gap-8 rounded-[20px] bg-white p-8 shadow-card lg:flex-row">
        <div className="flex flex-col items-center justify-center lg:w-[30%] lg:border-l lg:border-[#F4F8F8]">
          <p className="text-[56px] font-bold leading-none text-ghasla-text-heading">{reviewSummary.avg}</p>
          <div className="mt-2">
            <Stars rating={5} size="lg" />
          </div>
          <p className="mt-2 text-sm text-ghasla-text-secondary">من {reviewSummary.total} تقييم</p>
        </div>
        <div className="flex-1 space-y-2">
          {reviewSummary.breakdown.map((b) => (
            <div key={b.stars} className="flex items-center gap-3">
              <span className="w-6 whitespace-nowrap text-sm text-ghasla-text-primary">{b.stars}★</span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#F0F4F4]">
                <div className="h-full rounded-full bg-[#F59E0B]" style={{ width: `${b.pct}%` }} />
              </div>
              <span className="w-8 text-sm text-ghasla-text-secondary">{b.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-full bg-white p-1 shadow-card">
          {ratingFilters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                filter === f ? "bg-[#0D6E6E] text-white" : "text-ghasla-text-secondary hover:text-ghasla-text-link"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <Select
          value={service}
          onChange={setService}
          options={serviceOptions}
          className="w-[160px]"
        />
        <Select
          value={sort}
          onChange={setSort}
          options={sortOptions}
          className="w-[140px]"
        />
      </div>

      {/* Reviews list */}
      <div className="mt-5 space-y-3">
        {sorted.map((r) => (
          <div
            key={r.id}
            className={`rounded-2xl bg-white p-5 shadow-card ${
              r.rating <= 2 ? "border-l-4 border-l-[#E74C3C]" : ""
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0FDFD] text-sm font-semibold text-ghasla-text-link">
                  {r.name.slice(0, 2)}
                </div>
                <div>
                  <p className="text-[15px] font-semibold text-ghasla-text-heading">{r.name}</p>
                  <p className="text-xs text-ghasla-text-secondary">{r.date}</p>
                </div>
              </div>
              <Stars rating={r.rating} />
            </div>

            <p className="mt-3 text-[15px] text-ghasla-text-primary">{r.comment}</p>

            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-full bg-[#F0FDFD] px-3 py-1 text-xs font-medium text-ghasla-text-link">
                {r.service}
              </span>
              <span className="text-xs text-ghasla-text-secondary">{r.order}</span>
            </div>

            {/* Reply */}
            {r.replied && r.reply ? (
              <div className="mt-4 rounded-xl bg-[#F0FDFD] p-4">
                <p className="text-xs font-semibold text-ghasla-text-link">ردكم:</p>
                <p className="mt-1 text-sm text-ghasla-text-primary">{r.reply}</p>
                <button
                  type="button"
                  onClick={() => setReplyFor(r.id)}
                  className="mt-2 cursor-pointer text-xs text-ghasla-text-link hover:underline"
                >
                  تعديل
                </button>
              </div>
            ) : replyFor === r.id ? (
              <div className="mt-4">
                <textarea
                  className="min-h-[80px] w-full rounded-xl border border-[#E4EFEF] bg-[#F0FDFD] px-4 py-3 text-sm outline-none focus:border-[#0D6E6E]"
                  placeholder="اكتب ردك على التقييم..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => sendReply(r.id)}
                    className="cursor-pointer rounded-lg bg-[#0D6E6E] px-4 py-2 text-xs font-semibold text-white hover:opacity-90"
                  >
                    إرسال الرد
                  </button>
                  <button
                    type="button"
                    onClick={() => setReplyFor(null)}
                    className="cursor-pointer rounded-lg px-3 py-2 text-xs text-ghasla-text-secondary hover:bg-[#F0FDFD]"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setReplyFor(r.id);
                  setReplyText("");
                }}
                className="mt-3 cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline"
              >
                رد على التقييم
              </button>
            )}
          </div>
        ))}

        {sorted.length === 0 && (
          <div className="rounded-2xl bg-white p-10 text-center text-sm text-ghasla-text-secondary shadow-card">
            لا توجد تقييمات مطابقة
          </div>
        )}
      </div>
    </div>
  );
}